import {A11yAiProvider, PageContext} from './aiSuggest'

/** Collects up to `max` non-empty text samples from the cells below a given column. */
export function columnSamples(table: HTMLTableElement, colIndex: number, max = 5): string[] {
  const rows = Array.from(table.querySelectorAll('tr')).slice(1) // skip the header row
  const out: string[] = []
  for (const row of rows) {
    const cells = Array.from(row.querySelectorAll('td, th'))
    const text = cells[colIndex]?.textContent?.trim()
    if (text) out.push(text)
    if (out.length >= max) break
  }
  return out
}

/**
 * Fixes a table that has no header cells: promotes the first row's <td> cells to
 * <th scope="col">, preserving any existing text. The AI is asked for a label ONLY
 * for cells that are blank (and only when the provider supports headerLabel). The
 * header row is then wrapped in <thead> so the result matches the save-time processor.
 * Returns false if it could not run (no rows) so the caller can show a notice.
 */
export async function fixTableNoHeaders(
  table: HTMLTableElement,
  provider: A11yAiProvider,
  ctx?: PageContext
): Promise<boolean> {
  const firstRow = table.querySelector('tr')
  if (!firstRow) return false
  const doc = table.ownerDocument
  const cells = Array.from(firstRow.querySelectorAll('td, th'))
  if (cells.length === 0) return false

  for (let j = 0; j < cells.length; j++) {
    const cell = cells[j]
    const existing = cell.textContent?.trim() ?? ''
    let label = existing
    if (!label && provider.capabilities.headerLabel) {
      label = (await provider.suggestHeaderLabel(columnSamples(table, j), ctx)).text
    }
    const th = doc.createElement('th')
    th.setAttribute('scope', 'col')
    if (existing) th.innerHTML = cell.innerHTML // preserve any inline markup
    if (label && !th.textContent?.trim()) th.textContent = label
    cell.replaceWith(th)
  }

  if (!firstRow.closest('thead') && firstRow.parentNode) {
    const thead = doc.createElement('thead')
    firstRow.parentNode.insertBefore(thead, firstRow)
    thead.appendChild(firstRow)
  }
  return true
}

/** Fills a single empty <th> with an AI-suggested label drawn from its column. */
export async function fixEmptyHeader(
  th: Element,
  provider: A11yAiProvider,
  ctx?: PageContext
): Promise<boolean> {
  if (!provider.capabilities.headerLabel) return false
  const table = th.closest('table') as HTMLTableElement | null
  if (!table) return false
  const colIndex = th.parentElement ? Array.from(th.parentElement.children).indexOf(th) : 0
  const label = (await provider.suggestHeaderLabel(columnSamples(table, colIndex), ctx)).text
  if (!label) return false
  th.textContent = label
  return true
}

/** Sets (or replaces) a table's <caption> with an AI-suggested description. */
export async function fixTableCaption(
  table: HTMLTableElement,
  provider: A11yAiProvider,
  ctx?: PageContext
): Promise<boolean> {
  if (!provider.capabilities.tableCaption) return false
  const samples: string[] = []
  table.querySelectorAll('td, th').forEach((c) => {
    const t = c.textContent?.trim()
    if (t && samples.length < 8) samples.push(t)
  })
  const text = (await provider.suggestTableCaption(samples, ctx)).text
  if (!text) return false
  let caption = table.querySelector('caption')
  if (!caption) {
    caption = table.ownerDocument.createElement('caption')
    table.insertBefore(caption, table.firstChild)
  }
  caption.textContent = text
  return true
}
